import { openPaymentPage } from "@/lib/open-checkout";
import { buildDigitalConsent } from "@/lib/digital-consent";
import { checkoutAttribution } from "@/lib/checkout-attribution";
import { localeOrigin } from "@/lib/locale";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { MembershipFaq } from "@/components/MembershipFaq";
import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { readSelectedPlanId, setSelectedPlanId } from "@/lib/selected-plan";
import { PlanCarousel } from "@/components/membership/PlanCarousel";
import { trackPlanSelected } from "@/lib/pricing-analytics";
import { useMembership } from "@/hooks/useMembership";
import {
  createFoundingCheckoutSession,
  getMyMembership,
} from "@/lib/membership.functions";
import {
  createFoundingPaypalSubscription,
} from "@/lib/paypal-subscriptions.functions";
import {
  Crown,
  Sparkles,
  Loader2,
  ExternalLink,
  ShieldAlert,
  Check,
  XCircle,
  AlertTriangle,
  Info,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import { CreateAccountCTA } from "@/components/CreateAccountCTA";
import { MembershipErrorCard } from "@/components/MembershipErrorCard";
import { MemberMembershipView } from "@/components/membership/MemberMembershipView";
import { track } from "@/lib/track";
import {
  trackFunnel,
  startMembershipJourney,
  trackCheckoutFailed,
  MEMBERSHIP_SIGNUP_PATH,
} from "@/lib/funnel";
import { LocalPriceNote } from "@/components/LocalPrice";
import {
  MEMBERSHIP_BENEFITS,
  MEMBERSHIP_PRICE,
  MEMBERSHIP_PRICE_CENTS,
  DEFAULT_PLAN,
  getPlan,
  formatPlanPrice,
  type MembershipPlan,
} from "@/lib/membership-plans";
import { TherapistOfferSection } from "@/components/membership/TherapistOfferSection";
import { MembershipJourneySteps } from "@/components/membership/MembershipJourneySteps";
import { MembershipIncludedSection } from "@/components/membership/MembershipIncludedSection";
import { DailyCheckInSection } from "@/components/home/DailyCheckInSection";
import { Button } from "@/components/ui/button";
import { DigitalConsentCheckbox } from "@/components/DigitalConsentCheckbox";

import { z } from "zod";

export const Route = createFileRoute("/membership")({
  validateSearch: (search: Record<string, unknown>) =>
    z
      .object({
        cancelled: z.union([z.literal("1"), z.literal(1), z.boolean()]).optional(),
        failed: z.union([z.literal("1"), z.literal(1), z.boolean()]).optional(),
        already: z.union([z.literal("1"), z.literal(1), z.boolean()]).optional(),
        /** Set when someone on one-off access continues with full membership. */
        upgrade: z.string().optional(),
      })
      .parse(search),
  head: () => ({
    meta: [
      { title: "Membership | Recovery Trauma™" },
      {
        name: "description",
        content:
          "Join Recovery Trauma™ for $14.99 per month — full access to survivor-led trauma recovery tools, journals, live events and the member community.",
      },
      { property: "og:title", content: "Membership | Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Join Recovery Trauma™ for $14.99 per month — full access to survivor-led trauma recovery tools, journals, live events and the member community.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { property: "og:url", content: "https://recoverytrauma.com/membership" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/membership" }],
  }),

  component: MembershipPage,
});

/** Single source of truth for what membership includes. */
const BENEFITS = MEMBERSHIP_BENEFITS;


function MembershipPage() {
  const { loading, isAuthed, isAdmin, isMember, displayName, error, retry } =
    useMembership();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [gateChecked, setGateChecked] = useState(false);

  // Funnel step 1 — membership page viewed. Fired once the membership state is
  // known so we can separate guests from signed-in unpaid accounts.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (loading || viewedRef.current) return;
    viewedRef.current = true;
    track(
      "membership_page_viewed",
      { authed: isAuthed, is_member: isMember, is_admin: isAdmin },
      { source: "membership_page" },
    );
    trackFunnel(
      "membership_viewed",
      { source: "membership_page", plan: DEFAULT_PLAN.id },
      { authed: isAuthed, is_member: isMember, is_admin: isAdmin },
    );
  }, [loading, isAuthed, isMember, isAdmin]);

  // ── MEMBER VS GUEST ────────────────────────────────────────────────────
  // membership_status = active (isMember is only true with real payment
  // evidence — paid_at or a complimentary grant) → the page becomes the
  // member's overview + what's-new gateway. No sales copy, no checkout.
  // Guests / signed-in-unpaid keep the existing conversion page below.
  // Admins keep the sales page (plus the test panel) so checkout stays testable.
  const activeMember = !loading && isMember && !isAdmin;





  // Hard guard: block Stripe checkout unless the account exists AND the
  // email is verified. Admins bypass for internal testing.
  //
  // A transient getUser() failure (null user, network wedge, cold mobile
  // connection) must NEVER be interpreted as "email not verified" — that
  // was the bug that trapped a verified user on /verify-email. Prefer the
  // locally-stored session (carries email_confirmed_at synchronously) and
  // only fall through to a live check when we have to.
  useEffect(() => {
    if (loading || activeMember) return;
    let cancelled = false;
    (async () => {
      if (isAdmin) { if (!cancelled) setGateChecked(true); return; }
      if (!isAuthed) {
        // Session may still be hydrating (SSR render, cold mobile Safari,
        // Web-Locks contention). Ask Supabase directly — bounded — before
        // treating this viewer as a guest. Redirecting straight to /signup
        // trapped verified users who arrived at /membership on production.
        const { supabase } = await import("@/integrations/supabase/client");
        const { readStoredAuthSession } = await import("@/lib/auth-session");
        let session = readStoredAuthSession();
        if (!session) {
          session = await Promise.race<
            import("@supabase/supabase-js").Session | null
          >([
            supabase.auth
              .getSession()
              .then(({ data }) => data.session ?? null)
              .catch(() => null),
            new Promise((resolve) => setTimeout(() => resolve(null), 2500)),
          ]);
        }
        if (cancelled) return;
        if (session?.user?.id) {
          // Session is real — hold the spinner. onAuthStateChange will
          // flip useMembership's isAuthed and re-invoke this effect.
          return;
        }
        // Genuinely signed out — show the membership conversion page. The
        // primary CTA goes straight to checkout (pay first, account after).
        setGateChecked(true);
        return;

      }
      // Email verification no longer gates checkout — a signed-in account is
      // enough to continue to payment. The confirmation email still goes out.
      const { clearPendingVerifyEmail } = await import("@/lib/verify-status");
      clearPendingVerifyEmail();
      if (cancelled) return;
      setGateChecked(true);
      return;

    })();
    return () => { cancelled = true; };
  }, [loading, isAuthed, isAdmin, activeMember, navigate]);

  // ── ACTIVE MEMBER ──────────────────────────────────────────────────────
  // Rendered before every guest/verify branch so a paying member never sees
  // pricing, checkout or "Become a Member", not even for a frame.
  if (activeMember) {
    return <MemberMembershipView displayName={displayName} />;
  }



  // Signed-out visitors fall through to the main render below, which shows
  // the full membership benefits + pricing landing with the Create Account
  // CTA (step 2 of the journey). No separate guest card.


  if (loading || !gateChecked) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
        {/* Rendered server-side so the page always has one H1 for crawlers. */}
        <h1 className="font-display text-2xl text-foreground sm:text-3xl">
          Recovery Trauma™ Membership
        </h1>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }


  if (error && isAuthed) {
    return <MembershipErrorCard error={error} onRetry={retry} />;
  }

  const banner = search.cancelled
    ? {
        tone: "info" as const,
        icon: XCircle,
        title: "Payment cancelled",
        body: "You haven't been charged. Your place is still here whenever you're ready.",
      }
    : search.failed
      ? {
          tone: "warn" as const,
          icon: AlertTriangle,
          title: "Payment didn't complete",
          body: "Your card wasn't charged. Please try again, or use a different card below.",
        }
      : search.already
        ? {
            tone: "info" as const,
            icon: Info,
            title: "You're already a Member",
            body: "Sign in with the email you used to secure your membership to open your dashboard.",
          }
        : null;

  return (
    <div className="min-h-dvh bg-gradient-to-b from-[oklch(0.985_0.012_85)] via-background to-[oklch(0.96_0.03_82)] px-4 py-10 sm:py-16">
      <div className="mx-auto max-w-2xl">
        
        {banner ? <CheckoutBanner {...banner} /> : null}
        <FoundingMembershipCard isAuthed={isAuthed} />
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Not sure what's included?{" "}
          <Link
            to="/membership-benefits"
            className="font-medium text-[var(--gold-deep)] hover:underline"
          >
            See everything in your membership
          </Link>
        </p>

        <TherapistOfferSection />

        {/* Common questions — answered before they become a reason not to join. */}
        <section className="mt-10">
          <h2 className="mb-4 text-center font-display text-xl text-foreground">
            Questions people ask before joining
          </h2>
          <MembershipFaq />
        </section>

        {isAdmin ? <AdminTestPanel /> : null}

      </div>
    </div>
  );
}

function CheckoutBanner({
  tone,
  icon: Icon,
  title,
  body,
}: {
  tone: "info" | "warn";
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
}) {
  const cls =
    tone === "warn"
      ? "border-amber-400/60 bg-amber-50/70 text-amber-950"
      : "border-gold/40 bg-cream/70 text-foreground";
  return (
    <div
      role="status"
      className={`mb-6 flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-sm ${cls}`}
    >
      <Icon className="mt-0.5 h-5 w-5 flex-none" />
      <div className="text-sm leading-relaxed">
        <div className="font-medium">{title}</div>
        <p className="mt-0.5 opacity-85">{body}</p>
      </div>
    </div>
  );
}

function FoundingMembershipCard({ isAuthed }: { isAuthed: boolean }) {
  const navigate = useNavigate();
  const createCheckout = useServerFn(createFoundingCheckoutSession);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Express digital-content consent, captured before any payment button opens.
  const [digitalConsent, setDigitalConsent] = useState(false);
  // Which plan this person picked earlier in the journey (monthly by default).
  const [chosenPlan, setChosenPlan] = useState<MembershipPlan>(DEFAULT_PLAN);
  useEffect(() => {
    setChosenPlan(getPlan(readSelectedPlanId()));
  }, []);

  useEffect(() => {
    if (!isAuthed) return;
    trackFunnel(
      "page_view",
      { source: "membership_page", plan: chosenPlan.id, dedupeKey: "membership_payment_view" },
      { surface: "signed_in_payment_page" },
    );
  }, [isAuthed, chosenPlan.id]);

  /** Guest picked one of the three plans — carry it into signup. */
  function chooseGuestPlan(plan: MembershipPlan) {
    setSelectedPlanId(plan.id);
    setChosenPlan(plan);
    track(
      "plan_select",
      { plan: plan.id, price_usd: plan.priceCents / 100 },
      { source: "membership_page" },
    );
    trackPlanSelected(plan.id, plan.priceCents / 100, "membership_page");
    startMembershipJourney("membership_page_join", "membership_page");
    navigate({ to: "/signup", search: { plan: plan.id } });
  }


  async function handleJoin() {
    setErrorMsg(null);
    if (!digitalConsent) {
      setErrorMsg("Please tick the box to confirm you want immediate access before paying.");
      return;
    }
    // Funnel step 2 — Stripe button clicked.
    track(
      "join_clicked",
      {
        plan: DEFAULT_PLAN.id,
        price_usd: MEMBERSHIP_PRICE_CENTS / 100,
        authed: isAuthed,
        provider: "stripe",
      },
      { source: "membership_page" },
    );
    trackFunnel("join_clicked", {
      source: "membership_page",
      provider: "stripe",
      plan: DEFAULT_PLAN.id,
    });
    track(
      "plan_select",
      { plan: DEFAULT_PLAN.id, price_usd: MEMBERSHIP_PRICE_CENTS / 100 },
      { source: "membership_page" },
    );
    if (!isAuthed) {
      navigate({ to: "/choose-membership" });
      return;
    }
    setLoading(true);
    track("checkout_start", { plan: DEFAULT_PLAN.id }, { source: "membership_page" });
    trackFunnel("payment_started", {
      source: "membership_page",
      provider: "stripe",
      plan: DEFAULT_PLAN.id,
    });
    try {
      const origin = localeOrigin();
      const { readSelectedPlanId } = await import("@/lib/selected-plan");
      const res = await createCheckout({
        data: {
          plan_id: readSelectedPlanId() ?? undefined,
          digital_consent: buildDigitalConsent(),
          attribution: checkoutAttribution(),
          success_url: `${origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${origin}/checkout/cancelled?session_id={CHECKOUT_SESSION_ID}`,
        } as never,
      });
      if (res && (res as { ok?: boolean }).ok === true && (res as { url?: string }).url) {
        track(
          "checkout_session_created",
          { plan: DEFAULT_PLAN.id },
          { source: "membership_page" },
        );
        // Funnel step 4 — provider checkout actually opened.
        track(
          "checkout_opened",
          { plan: DEFAULT_PLAN.id, provider: "stripe" },
          { source: "membership_page" },
        );
        trackFunnel("checkout_opened", {
          source: "membership_page",
          provider: "stripe",
          plan: DEFAULT_PLAN.id,
        });
        openPaymentPage((res as { url: string }).url);
        return;
      }
      const status = (res as { status?: string })?.status ?? "unknown";
      const message =
        (res as { message?: string })?.message ??
        "We couldn't start checkout. Please try again in a moment.";
      track("checkout_blocked", { status }, { source: "membership_page" });
      trackCheckoutFailed({
        source: "membership_page",
        provider: "stripe",
        plan: DEFAULT_PLAN.id,
        price_usd: MEMBERSHIP_PRICE_CENTS / 100,
        reason: message ?? status,
        stage: "session_create",
        fallback: "stay_on_membership_page",
      });
      setErrorMsg(message);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[checkout] createFoundingCheckoutSession failed:", e);
      track("checkout_error", { message: msg.slice(0, 120) }, { source: "membership_page" });
      trackCheckoutFailed({
        source: "membership_page",
        provider: "stripe",
        plan: DEFAULT_PLAN.id,
        price_usd: MEMBERSHIP_PRICE_CENTS / 100,
        reason: msg,
        stage: "session_create",
        fallback: "stay_on_membership_page",
      });
      // Surface the real reason so we (and the user) can act on it instead of
      // showing a generic "something went wrong".
      const friendly =
        msg && msg !== "[object Object]" && msg.length < 400
          ? msg
          : "Something went wrong starting checkout. Please try again.";
      setErrorMsg(friendly);
    } finally {
      setLoading(false);
    }
  }

  // ─────────────── Authenticated: payment-first activation card ───────────────
  if (isAuthed) {
    return (
      <div className="relative overflow-hidden rounded-[2rem] border-2 border-gold/40 bg-card p-6 shadow-[var(--shadow-gold)] sm:p-10">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -end-24 h-64 w-64 rounded-full bg-[var(--gradient-gold)] opacity-20 blur-3xl"
        />

        <div className="relative text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <Crown className="h-5 w-5 text-foreground" strokeWidth={2.25} />
          </div>
          <p className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/60 px-3 py-1.5 text-[12px] font-medium text-foreground dark:bg-cream/10">
            <span aria-hidden>•</span>
            One step left — your account is ready
          </p>
          <h1 className="mt-4 font-display text-2xl leading-tight text-foreground sm:text-3xl">
            Activate your Membership
          </h1>
          <div className="mt-4 flex items-baseline justify-center gap-2">
            <span className="font-serif text-5xl text-foreground sm:text-6xl">
              {formatPlanPrice(chosenPlan)}
            </span>
            <span className="text-base text-muted-foreground">
              {chosenPlan.interval === "year"
                ? "/ year"
                : chosenPlan.intervalCount === 1
                  ? "/ month"
                  : `/ ${chosenPlan.intervalCount} months`}
            </span>
          </div>
          <LocalPriceNote cents={chosenPlan.priceCents} className="mt-1 text-xs text-muted-foreground" />
          <p className="mt-1 text-xs text-muted-foreground">{chosenPlan.billingNote}</p>
          <Link
            to="/choose-membership"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)] hover:underline"
          >
            See all three plans
            <ChevronRight className="h-4 w-4" />
          </Link>
          <p className="mt-1 text-xs text-muted-foreground">
            Secure payment by Stripe or PayPal
          </p>
        </div>

        {/* Express consent first — the payment buttons stay off until it is ticked. */}
        <div className="relative mt-5 rounded-xl border border-gold/25 bg-cream/50 p-3 dark:bg-cream/5">
          <DigitalConsentCheckbox checked={digitalConsent} onChange={setDigitalConsent} id="digital-consent-membership" />
        </div>

        {/* Payment first — nothing above the fold competes with these two buttons. */}
        <div className="relative mt-7 space-y-3">
          <button
            type="button"
            onClick={handleJoin}
            disabled={loading || !digitalConsent}
            className="group flex w-full items-center gap-4 rounded-2xl bg-[oklch(0.28_0.06_150)] px-4 py-5 text-start text-white shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] transition hover:brightness-110 disabled:opacity-70 sm:px-5"
          >
            <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white shadow-sm dark:bg-card">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin text-[oklch(0.28_0.06_150)]" />
              ) : (
                <CreditCard className="h-5 w-5 text-[oklch(0.28_0.06_150)]" strokeWidth={2.25} />
              )}
            </span>
            <span className="flex-1">
              <span className="block text-base font-semibold leading-tight sm:text-[17px]">
                Pay by Card
              </span>
              <span className="mt-0.5 block text-[13px] text-white/75">
                Secure card checkout with Stripe
              </span>
            </span>
            <ChevronRight className="h-5 w-5 flex-none text-white/80 transition group-hover:translate-x-0.5" />
          </button>

          {errorMsg ? (
            <p className="text-center text-sm text-destructive" role="alert">
              {errorMsg}
            </p>
          ) : null}

          <PaypalCheckoutButton consentGiven={digitalConsent} />

          <p className="pt-1 text-center text-xs leading-relaxed text-muted-foreground">
            Secure payment processed by Stripe or PayPal.
          </p>

        </div>

        {/* Value proposition — why this is worth $14.99, right below the buttons. */}
        <div className="relative mt-8 rounded-2xl border border-gold/25 bg-cream/50 p-5 dark:bg-cream/5">
          <p className="font-display text-base text-foreground">
            What your membership unlocks today
          </p>
          <ul className="mt-3 space-y-2.5">
            {BENEFITS.slice(0, 6).map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5">
                <span className="mt-0.5 flex h-4 w-4 flex-none items-center justify-center rounded-full bg-[var(--gradient-gold)]">
                  <Check className="h-2.5 w-2.5 text-foreground" strokeWidth={3} />
                </span>
                <span className="text-sm leading-relaxed text-foreground/85">{benefit}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/membership-benefits"
            className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--gold-deep)] hover:underline"
          >
            See everything included
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <PreLaunchBillingNotice />

        <div className="relative mt-6 text-center">
          <Link
            to="/dashboard"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 bg-card/80 px-5 py-2 text-sm font-semibold text-[var(--gold-deep)] transition hover:border-gold hover:bg-cream/60"
          >
            <span aria-hidden>←</span> Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  // ─────────────── Guest: full marketing card ───────────────
  return (
    <div className="relative overflow-hidden rounded-[2rem] border-2 border-gold/40 bg-card p-5 shadow-[var(--shadow-gold)] sm:p-12">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -end-24 h-64 w-64 rounded-full bg-[var(--gradient-gold)] opacity-20 blur-3xl"
      />
      <div className="relative text-center">
        <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)] sm:h-14 sm:w-14">
          <Crown className="h-5 w-5 text-foreground sm:h-6 sm:w-6" strokeWidth={2.25} />
        </div>
        <span className="mt-4 inline-flex min-h-9 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-cream/60 px-3 py-1 text-xs font-medium tracking-wide text-foreground dark:bg-cream/10">
          <Sparkles className="h-3 w-3 text-[var(--gold-deep)]" />
          Membership
        </span>
        <h1 className="mt-3 font-display text-[1.6rem] leading-snug text-foreground sm:text-4xl sm:leading-tight">
          Everything you need for your recovery, in one calm place
        </h1>
        <p className="mx-auto mt-2.5 max-w-md text-[0.85rem] leading-relaxed text-foreground/70 sm:text-base">
          Survivor-led tools, private journals, live workshops and a community that
          understands — Recovery Trauma<span className="text-[var(--gold-deep)]">™</span> membership.
        </p>
        <p className="mt-3 text-xs text-muted-foreground">
          Three ways to pay — same full access on every plan. Cancel anytime.
        </p>
      </div>

      {/* All three plans, visible immediately — nothing hidden off-screen. */}
      <div className="relative">
        <PlanCarousel onChoose={chooseGuestPlan} source="membership_page" />
      </div>

      <MembershipJourneySteps current={1} className="relative mt-7" />

      {/* Single membership journey: create account → verify email → choose
          Stripe or PayPal → complete payment → access membership. */}
      <div className="relative mt-7 space-y-3">
        <Link
          to={MEMBERSHIP_SIGNUP_PATH}
          onClick={() => {
            track(
              "join_clicked",
              {
                plan: "membership_monthly",
                price_usd: MEMBERSHIP_PRICE_CENTS / 100,
                authed: false,
                flow: "membership_journey",
              },
              { source: "membership_page" },
            );
            startMembershipJourney("membership_page_join", "membership_page");
          }}
          className="group flex w-full items-center gap-4 rounded-2xl bg-[oklch(0.28_0.06_150)] px-4 py-5 text-start text-white shadow-[0_10px_28px_-12px_rgba(0,0,0,0.4)] transition hover:brightness-110 sm:px-5"
        >
          <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white shadow-sm dark:bg-card">
            <CreditCard className="h-5 w-5 text-[oklch(0.28_0.06_150)]" strokeWidth={2.25} />
          </span>
          <span className="flex-1">
            <span className="block text-base font-semibold uppercase leading-tight tracking-wide sm:text-[17px]">
              Create Account
            </span>
            <span className="mt-0.5 block text-[13px] text-white/75">
              Takes about a minute — then choose Stripe or PayPal
            </span>
          </span>
          <ChevronRight className="h-5 w-5 flex-none text-white/80 transition group-hover:translate-x-0.5" />
        </Link>

        {/* Reassurance row — the three objections that stop people paying. */}
        <ul className="grid grid-cols-1 gap-2 pt-1 sm:grid-cols-3">
          {[
            "Cancel anytime, in one click",
            "Card details never touch us",
            "Written by survivors, not marketers",
          ].map((item) => (
            <li
              key={item}
              className="flex items-center gap-2 rounded-xl border border-gold/25 bg-cream/50 px-3 py-2 text-[12px] leading-snug text-foreground/85 dark:bg-cream/5"
            >
              <Check className="h-3.5 w-3.5 flex-none text-[var(--gold-deep)]" strokeWidth={3} />
              {item}
            </li>
          ))}
        </ul>

        <p className="text-center text-xs leading-relaxed text-muted-foreground">
          Payment is handled entirely by Stripe or PayPal.
        </p>

      </div>

      <DailyCheckInSection />

      <MembershipIncludedSection />

      <p className="relative mt-5 text-center text-sm leading-relaxed text-muted-foreground">
        Membership is a monthly subscription. It renews automatically each month
        until you cancel, and you keep access until the end of the period you
        have paid for.
      </p>



      <PreLaunchBillingNotice />

      <div className="relative mt-8">
        <div className="mt-5 flex flex-col items-center gap-3 text-center">
          <Link
            to="/"
            className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            ← Back to home
          </Link>
        </div>

      </div>


    </div>
  );
}

function PreLaunchBillingNotice() {
  // Pre-launch (before 1 Aug 2026) — Stripe subscription is anchored to
  // 1 Sep 2026, first charge today, second charge on 1 Sep. Keep this
  // message pinned so the customer sees the exact billing timeline
  // BEFORE they land on Stripe's hosted checkout.
  const now = Date.now();
  const launchAt = Date.UTC(2026, 7, 1, 0, 0, 0);
  if (now >= launchAt) return null;
  return (
    <div className="relative mt-8 rounded-2xl border border-gold/40 bg-cream/70 p-4 text-sm leading-relaxed text-foreground/85">
      <p className="font-medium text-foreground">Before you pay</p>
      <ul className="mt-2 space-y-1.5 list-disc ps-5">
        <li>
          Today you'll pay <strong>$14.99</strong> to secure your Membership.
        </li>
        <li>
          You can continue exploring the platform with Guest Access immediately.
        </li>
        <li>
          Your next payment will be taken on <strong>1 September 2026</strong>,
          then monthly on the 1st while your membership remains active.
        </li>
      </ul>
    </div>
  );
}

function AdminTestPanel() {
  const createCheckout = useServerFn(createFoundingCheckoutSession);
  const fetchMine = useServerFn(getMyMembership);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<
    ReturnType<typeof createFoundingCheckoutSession>
  > | null>(null);
  const [mine, setMine] = useState<Awaited<ReturnType<typeof getMyMembership>> | null>(null);
  const [targetEmail, setTargetEmail] = useState("");

  useEffect(() => {
    fetchMine({ data: undefined as never }).then(setMine).catch(() => {});
  }, [fetchMine]);

  async function startCheckout() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await createCheckout({
        data: targetEmail
          ? { target_email: targetEmail }
          : ({ digital_consent: buildDigitalConsent() } as never),
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <details className="mt-10 rounded-2xl border border-amber-400/40 bg-amber-50/40 p-4 text-sm">
      <summary className="flex cursor-pointer items-center gap-2 font-medium text-amber-900">
        <ShieldAlert className="h-4 w-4" />
        Admin — Stripe test panel
      </summary>
      <div className="mt-4 space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Test as email (optional)
          <input
            type="email"
            value={targetEmail}
            onChange={(e) => setTargetEmail(e.target.value)}
            placeholder="tester@example.com"
            className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
          />
        </label>
        <button
          onClick={startCheckout}
          disabled={loading}
          className="btn-gold w-full justify-center text-sm"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start test checkout"}
        </button>
        {error ? <p className="text-sm text-destructive">Error: {error}</p> : null}
        {result && result.ok === false ? (
          <p className="text-sm text-amber-800">
            {result.message} (status: <code>{result.status}</code>)
          </p>
        ) : null}
        {result && result.ok === true ? (
          <a
            href={result.url ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl border border-gold/40 bg-background px-4 py-2 text-sm font-medium text-foreground hover:border-gold"
          >
            Open Stripe checkout <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : null}
        <div className="rounded-xl border border-border bg-card/60 p-3">
          <div className="mb-2 text-xs font-medium text-foreground">Your membership row</div>
          {mine ? (
            <pre className="max-h-48 overflow-auto rounded-lg bg-muted/40 p-2 text-[11px]">
              {JSON.stringify(mine, null, 2)}
            </pre>
          ) : (
            <p className="text-xs text-muted-foreground">No membership row yet.</p>
          )}
        </div>
        <Link to="/admin/membership" className="block text-[var(--gold-deep)] underline">
          Open admin membership console →
        </Link>
      </div>
    </details>
  );
}

// -------- PayPal button --------
// Sits below the Stripe button. Only renders when the user is signed-in AND
// the visibility server-fn says so (admin, or the public feature flag is on).
// Never activates access itself — approval hands off to /paypal/return, and
// the real activation happens in the webhook.
export function PaypalCheckoutButton({
  planId,
  voucherCode,
  consentGiven = true,
}: { planId?: string; voucherCode?: string | null; consentGiven?: boolean } = {}) {
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const startFn = useServerFn(createFoundingPaypalSubscription);
  // Fall back to the plan the visitor picked earlier in the journey rather
  // than to monthly — otherwise PayPal opens a cheaper plan than they chose.
  const [storedPlanId, setStoredPlanId] = useState<string | null>(null);
  useEffect(() => {
    setStoredPlanId(readSelectedPlanId());
  }, []);
  const selectedPlanId = planId ?? storedPlanId ?? DEFAULT_PLAN.id;

  async function handleClick() {
    setMsg(null);
    if (!consentGiven) {
      setMsg("Please tick the box to confirm you want immediate access before paying.");
      return;
    }
    setStarting(true);
    // Funnel step 3 — PayPal button clicked.
    track("checkout_paypal_clicked", { plan: selectedPlanId }, { source: "membership_page" });
    trackFunnel("join_clicked", {
      source: "membership_page",
      provider: "paypal",
      plan: selectedPlanId,
    });
    trackFunnel("payment_started", {
      source: "membership_page",
      provider: "paypal",
      plan: selectedPlanId,
    });
    try {
      const origin = localeOrigin();
      const res = await startFn({
        data: {
          return_url: `${origin}/paypal/return`,
          // Cancelling at PayPal brings them back to the page that still
          // offers card checkout, so a mis-tap is never a dead end.
          cancel_url: `${origin}/complete-membership?cancelled=1`,
          plan_id: selectedPlanId,
          attribution: checkoutAttribution(),
          digital_consent: buildDigitalConsent(),
          ...(voucherCode ? { voucher_code: voucherCode } : {}),
        } as never,
      });

      if (res && (res as { ok?: boolean }).ok && (res as { approval_url?: string }).approval_url) {
        // Funnel step 4 — provider checkout actually opened.
        track(
          "checkout_opened",
          { plan: selectedPlanId, provider: "paypal" },
          { source: "membership_page" },
        );
        trackFunnel("checkout_opened", {
          source: "membership_page",
          provider: "paypal",
          plan: selectedPlanId,
        });
        openPaymentPage((res as { approval_url: string }).approval_url);
        return;
      }
      track(
        "checkout_blocked",
        { provider: "paypal" },
        { source: "membership_page" },
      );
      setMsg((res as { message?: string })?.message ?? "PayPal checkout is unavailable right now.");
    } catch (e) {
      setMsg(e instanceof Error ? e.message.slice(0, 300) : "PayPal request failed.");
    } finally {
      setStarting(false);
    }
  }

  return (
    <>
      <Button
        type="button"
        variant="goldOutline"
        onClick={handleClick}
        disabled={starting || !consentGiven}
        className="group grid min-h-[64px] w-full grid-cols-[auto_minmax(0,1fr)_auto] gap-3 overflow-hidden whitespace-normal rounded-xl bg-card px-4 text-start shadow-sm disabled:opacity-60"
        aria-label="Pay with PayPal"
      >
        <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-white shadow-sm dark:bg-card">
          {starting ? (
            <Loader2 className="h-5 w-5 animate-spin text-[#003087]" />
          ) : (
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
              <path fill="#003087" d="M7.5 20.5H5.2c-.3 0-.5-.2-.4-.5L6.9 4.6c.05-.3.3-.5.6-.5h5.4c2.7 0 4.4 1.4 4 4-.5 3-2.9 4.6-6 4.6H9.2c-.3 0-.5.2-.6.5l-.7 6.7c-.05.3-.2.6-.4.6z"/>
              <path fill="#0070ba" d="M9.6 22h-2.3c-.3 0-.5-.2-.4-.5L8.9 6.1c.05-.3.3-.5.6-.5h5.4c2.7 0 4.4 1.4 4 4-.5 3-2.9 4.6-6 4.6h-1.6c-.3 0-.5.2-.6.5l-.7 6.7c-.05.4-.2.6-.4.6z"/>
            </svg>
          )}
        </span>
        <span className="flex-1">
          <span className="block text-[15px] font-semibold leading-tight text-foreground sm:text-base">
            Pay with PayPal
          </span>
          <span className="mt-0.5 block text-[13px] text-foreground/70">
            Pay securely with your PayPal account
          </span>
        </span>
        <ChevronRight className="h-5 w-5 flex-none text-foreground/70 transition group-hover:translate-x-0.5" />
      </Button>
      {msg ? (
        <div className="text-center" role="alert">
          <p className="text-sm text-destructive">{msg}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            You can still pay by card with the card button above — nothing has been charged.
          </p>
        </div>
      ) : null}
    </>
  );
}

